import { type z } from 'zod';

import type { RequestInput } from '../types';
import { HttpMethod, MIME_TYPE } from '../types';
import { urlWithLocale } from './locale';
import { evaluateSession } from './handleSession';
import { logger } from './logger';

const logsPrefix = '[typedFetch]';

const logError = (
    response: Response,
    parsedResponse: unknown,
    withLogs: boolean
) => {
    try {
        if (withLogs) {
            console.error(
                logsPrefix,
                response.url,
                response.status,
                parsedResponse
            );
        }
    } catch {
        if (withLogs) {
            console.error(logsPrefix, response.url, response.status);
        }
    }
};

const logOrderUrl = (url: RequestInput) => url.toString().includes('order');

const logSuccess = async (
    url: string,
    response?: Response,
    init?: RequestInit,
    withLogs?: boolean
) => {
    if (withLogs) {
        const logs = `${logsPrefix} ${
            init?.method ?? HttpMethod.GET
        } - ${url} [${response?.status ?? 500}]`;

        console.info(`${logs}`);
    }
};

const validationCheck = (
    zodSchema: z.ZodTypeAny | undefined,
    url: string,
    status: number,
    responseJson: unknown,
    method: string | HttpMethod = HttpMethod.GET
) => {
    if (zodSchema) {
        const zodResult = zodSchema.safeParse(responseJson);

        if (!zodResult.success) {
            console.error(
                '[validationError]',
                `${method} - ${url.toString()} [${status}]`,
                '\nerrors:',
                JSON.stringify(zodResult.error.errors, null, 2),
                '\nresponse:',
                JSON.stringify(responseJson, null, 2)
            );
        }
    }
};

const conditionalLog = (
    shouldLog: boolean,
    logLevel: 'info' | 'error',
    ...args: unknown[]
) => {
    if (shouldLog || logLevel === 'error') {
        logger[logLevel](...args);
    }
};
async function parseResponse<T>({
    response,
    isJson,
    zodSchema,
    url,
    method = HttpMethod.GET,
}: {
    response: Response;
    isJson?: boolean;
    zodSchema?: z.ZodTypeAny;
    url: string;
    method?: string | HttpMethod;
}) {
    if (isJson) {
        const responseJson = (await response.json()) as T;

        validationCheck(zodSchema, url, response.status, responseJson, method);

        return responseJson;
    }

    return (await response.text()) as T;
}

// Each fetching attempt is triggered and the timeout to validate that the API call does not exceed its own time are here
const attemptFetch = async (
    attempt: number,
    url: RequestInput,
    timeoutDuration?: number,
    init?: RequestInit & {
        additionalSuccessCodes?: number[];
        withLocale?: boolean;
        withLogs?: boolean;
    }
): Promise<Response | null> => {
    const controller = new AbortController();
    // when timeoutDuration is defined that means each attempt should have an expiration time
    const abortSignalId = timeoutDuration
        ? setTimeout(() => controller.abort(), timeoutDuration)
        : undefined;

    const shouldLog = logOrderUrl(url);
    try {
        conditionalLog(
            shouldLog,
            'info',
            'coreFetch attempt %d for URL: %s, %o',
            attempt,
            url.toString(),
            init
        );
        const requestInit = {
            ...init,
            signal: abortSignalId ? controller.signal : init?.signal,
        };
        return await fetch(url, requestInit);
    } catch (error) {
        if (error instanceof Error) {
            if (error.name === 'AbortError') {
                conditionalLog(
                    shouldLog,
                    'error',
                    '[ >> coreFetch << ] Fetch aborted due to timeout: %d ms for URL: %s, %o',
                    timeoutDuration,
                    url.toString(),
                    init
                );
            }
        } else {
            conditionalLog(
                shouldLog,
                'error',
                '[ >> coreFetch << ] Unknown error occurred during fetch. %s, %o ',
                url.toString(),
                init
            );
        }

        return null;
    } finally {
        clearTimeout(abortSignalId);
    }
};

// Helper function to prepare request parameters
const prepareRequest = (
    input: RequestInput,
    init?: RequestInit & {
        additionalSuccessCodes?: number[];
        withLocale?: boolean;
        withLogs?: boolean;
    }
) => {
    const withLogs = init?.withLogs ?? typeof window === 'undefined';
    const url = init?.withLocale ? urlWithLocale(input) : input;
    const additionalSuccessCodes = init?.additionalSuccessCodes ?? [];
    return { withLogs, url, additionalSuccessCodes };
};

// Handle retries logic -- Loop to re-try a new fetch
const handleRetries = async (
    url: RequestInfo,
    init?: RequestInit & {
        additionalSuccessCodes?: number[];
        withLocale?: boolean;
        withLogs?: boolean;
    },
    numberOfRetries?: number,
    timeoutDuration?: number
) => {
    let response: Response | null = null;

    const shouldLog = logOrderUrl(url);
    const attempts = numberOfRetries || 0;
    // if the number of numberOfRetries is defined it time duration should have a default value of 3 seconds
    const attemptDuration =
        attempts > 0 && timeoutDuration === undefined ? 3000 : timeoutDuration;
    for (let attempt = 0; attempt <= attempts; attempt++) {
        response = await attemptFetch(attempt, url, attemptDuration, init);
        if (response) {
            break;
        }

        conditionalLog(
            shouldLog,
            'info',
            '[ >> coreFetch << ] Retrying fetch... Attempt %d for URL: %s, %o',
            attempt,
            url.toString(),
            init
        );
    }

    if (!response) {
        conditionalLog(
            shouldLog,
            'error',
            '[ >> coreFetch << ] coreFetch failed after %d attempts for URL: %s, %o',
            numberOfRetries,
            url.toString(),
            init
        );
    }
    return response;
};

// Parse and validate response
const parseAndValidateResponse = async <T>(
    response: Response,
    zodSchema: z.ZodTypeAny | undefined,
    url: string,
    init?: RequestInit
) => {
    const contentType = response.headers.get('Content-Type');
    const isJson = contentType?.includes(MIME_TYPE.json);
    return await parseResponse<T>({
        response,
        isJson,
        zodSchema,
        url,
        method: init?.method,
    });
};

// Handle response status and return parsed data or error
const handleResponseCode = <T>(
    response: Response,
    parsedResponse: T,
    url: string,
    additionalSuccessCodes: number[],
    withLogs: boolean,
    init?: RequestInit & {
        additionalSuccessCodes?: number[];
        withLocale?: boolean;
        withLogs?: boolean;
    }
): T | null => {
    if (response.status === 204) {
        // HTTP 204 means a successful empty response
        return null;
    }
    const isSuccess =
        (response.status >= 200 && response.status < 300) ||
        additionalSuccessCodes.includes(response.status);
    if (isSuccess) {
        logSuccess(url, response, init, withLogs);
        return parsedResponse;
    }

    logError(response, parsedResponse, withLogs);

    return null;
};
// Handle response processing
const processFetchResponse = async <T>({
    fetchResponse,
    url,
    init,
    zodSchema,
    additionalSuccessCodes,
    withLogs,
}: {
    fetchResponse: Response | null;
    url: string;
    init?: RequestInit & {
        additionalSuccessCodes?: number[];
        withLocale?: boolean;
        withLogs?: boolean;
    };
    zodSchema?: z.ZodTypeAny;
    additionalSuccessCodes: number[];
    withLogs: boolean;
}): Promise<T | null> => {
    const shouldLog = logOrderUrl(url);
    conditionalLog(
        shouldLog,
        'info',
        '[ >> coreFetch << ] before parse. %o',
        fetchResponse
    );
    if (!fetchResponse) {
        return null;
    }

    try {
        const parsedResponse = await parseAndValidateResponse<T>(
            fetchResponse,
            zodSchema,
            url,
            init
        );

        conditionalLog(
            shouldLog,
            'info',
            '[ >> coreFetch << ] after parse. %o',
            parsedResponse
        );

        conditionalLog(
            shouldLog,
            'info',
            '[ >> coreFetch << ] additionalSuccessCodes. %o',
            additionalSuccessCodes
        );
        return handleResponseCode(
            fetchResponse,
            parsedResponse,
            url,
            additionalSuccessCodes,
            withLogs,
            init
        );
    } catch (error) {
        if (error instanceof Error) {
            conditionalLog(
                shouldLog,
                'error',
                '[ >> coreFetch << ] Error processing fetch response: %s, %o, %d',
                error.message,
                {
                    stack: error.stack,
                },
                additionalSuccessCodes
            );
        } else {
            conditionalLog(
                shouldLog,
                'error',
                '[ >> coreFetch << ] Unknown error occurred during fetch. %d',
                additionalSuccessCodes
            );
        }
        return null;
    }
};

export const coreFetch = async <T>(
    input: RequestInput,
    init?: RequestInit & {
        additionalSuccessCodes?: number[];
        withLocale?: boolean;
        withLogs?: boolean;
    },
    zodSchema?: z.ZodTypeAny,
    numberOfRetries?: number,
    timeoutDuration?: number
): Promise<T | null> => {
    const { withLogs, url, additionalSuccessCodes } = prepareRequest(
        input,
        init
    );

    const shouldLog = logOrderUrl(url);
    conditionalLog(
        shouldLog,
        'info',
        '[ >> coreFetch << ] before call. %s, %o',
        url.toString(),
        init
    );

    // Is Client Side?
    if (typeof window !== 'undefined') {
        await evaluateSession();
    }

    const fetchResponse = await handleRetries(
        url.toString(),
        init,
        numberOfRetries,
        timeoutDuration
    );

    conditionalLog(
        shouldLog,
        'info',
        '[ >> coreFetch << ] after call. %o',
        fetchResponse
    );
    return processFetchResponse<T>({
        fetchResponse,
        url: url.toString(),
        init,
        zodSchema,
        additionalSuccessCodes,
        withLogs,
    });
};
